import { useEffect, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
  shorthands,
} from '@fluentui/react-components';
import { listContextHelp, type ContextHelpText } from '../api/contextHelp.js';

/**
 * Ayuda contextual por campo — spec "Ayuda Contextual Contexto de Obra"
 * (2026-07-02). Icono «?» junto a la etiqueta → popover por CLIC (no hover:
 * requisito táctil), 4 bloques fijos: Qué es / Cómo llenarlo / Ejemplo / Afecta.
 *
 * Reutilizable: los textos vienen de /api/v1/context-help (tabla global
 * editable por Soporte/CSM); este componente sirve para cualquier pantalla
 * (Calibración después). Fluent Popover cubre el patrón disclosure WAI-ARIA:
 * un popover a la vez, Esc / clic fuera, foco al abrir y retorno al cerrar.
 *
 * REGLA (spec §4): los textos nunca muestran %, rangos ni fórmulas de factores.
 */

// ---- cache de textos a nivel módulo: una sola petición por sesión ----
let helpCache: Map<string, ContextHelpText> | null = null;
let helpPromise: Promise<Map<string, ContextHelpText>> | null = null;
// Suscriptores para re-render cuando el cache se invalida/recarga (self-heal de keys nuevas).
const listeners = new Set<() => void>();
// Keys que ya provocaron un reintento (para no recargar en bucle si de verdad no existen).
const reintentadas = new Set<string>();
function notificar(): void {
  for (const l of listeners) l();
}

async function loadHelp(): Promise<Map<string, ContextHelpText>> {
  if (helpCache) return helpCache;
  helpPromise ??= listContextHelp()
    .then((texts) => {
      helpCache = new Map(texts.map((t) => [t.key, t]));
      return helpCache;
    })
    .catch(() => {
      helpPromise = null; // permitir reintento en el siguiente uso
      return new Map<string, ContextHelpText>();
    });
  return helpPromise;
}

/** Invalida el cache (la usa el editor staff tras guardar). */
export function invalidateContextHelpCache(): void {
  helpCache = null;
  helpPromise = null;
}

/**
 * Si un consumidor pide una key que NO está en el cache, es probable que se haya agregado un texto de
 * ayuda DESPUÉS de que se cargó la lista (migración nueva, o el HMR de dev preservó el cache viejo).
 * Recargamos UNA vez por key y avisamos a los suscriptores. Sin bucle: si tras recargar sigue sin
 * estar, no se reintenta más (esa key de verdad no existe → el "?" no se muestra, falla suave).
 */
function autoSanarKeyFaltante(key: string): void {
  if (reintentadas.has(key)) return;
  reintentadas.add(key);
  invalidateContextHelpCache();
  void loadHelp().then(() => notificar());
}

export function useContextHelp(): Map<string, ContextHelpText> | null {
  const [map, setMap] = useState<Map<string, ContextHelpText> | null>(helpCache);
  const [, bump] = useState(0);
  useEffect(() => {
    const l = () => bump((n) => n + 1);
    listeners.add(l);
    let cancelled = false;
    void loadHelp().then((m) => { if (!cancelled) setMap(m); });
    return () => { cancelled = true; listeners.delete(l); };
  }, []);
  // Reflejar el cache actual (por si otro consumidor lo recargó).
  return helpCache ?? map;
}

const useStyles = makeStyles({
  trigger: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    borderRadius: '50%',
    border: '1.5px solid var(--border-default)',
    background: 'var(--bg-surface)',
    color: 'var(--text-tertiary)',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
    ':hover': {
      ...shorthands.borderColor('var(--accent)'),
      color: 'var(--accent)',
      background: 'var(--accent-weak)',
    },
    '&[aria-expanded="true"]': {
      ...shorthands.borderColor('var(--accent)'),
      color: 'var(--accent)',
      background: 'var(--accent-weak)',
    },
  },
  surface: {
    width: '340px',
    maxWidth: 'calc(100vw - 24px)',
    padding: '16px 18px 14px',
  },
  title: { margin: '0 0 10px', fontSize: '14.5px', lineHeight: 1.35, fontWeight: 600 },
  kicker: {
    display: 'block',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    margin: '11px 0 3px',
  },
  body: { margin: 0, fontSize: '13.5px', lineHeight: 1.55, color: 'var(--text-secondary)' },
  ejemplo: { fontStyle: 'italic', color: 'var(--text-tertiary)' },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    marginTop: '12px',
    paddingTop: '11px',
    borderTop: '1px solid var(--border-subtle)',
  },
  chipsLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--text-tertiary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginRight: '2px',
  },
  chip: {
    padding: '3px 9px',
    borderRadius: 'var(--radius-circular)',
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    fontSize: '11.5px',
    fontWeight: 600,
  },
});

/**
 * Icono «?» con popover de ayuda. No renderiza nada si el texto de la key no
 * existe (falla suave: sin ayuda ≠ pantalla rota).
 */
export function FieldHelp({ helpKey, variante = 'campo' }: { helpKey: string; variante?: 'campo' | 'accion' }) {
  const styles = useStyles();
  const map = useContextHelp();
  const d = map?.get(helpKey);
  // Si la lista ya cargó pero esta key no está, intentar recargar UNA vez (texto agregado después).
  useEffect(() => {
    if (map && !map.get(helpKey)) autoSanarKeyFaltante(helpKey);
  }, [map, helpKey]);
  if (!d) return null;
  // En BOTONES/acciones "Cómo llenarlo" no aplica (un botón no se llena) → "Cómo se usa".
  const etiquetaMedio = variante === 'accion' ? 'Cómo se usa' : 'Cómo llenarlo';

  return (
    <Popover withArrow positioning={{ position: 'below', align: 'center', flipBoundary: undefined }}>
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={styles.trigger} aria-label={`Ayuda: ${d.title}`}>
          ?
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface} aria-label={d.title}>
        <h3 className={styles.title}>{d.title}</h3>
        <span className={styles.kicker}>Qué es</span>
        <p className={styles.body}>{d.queEs}</p>
        {/* Una ayuda puede no tener ejemplo ni "afecta": esas secciones se omiten, no quedan vacías. */}
        {d.comoLlenarlo?.trim() && (
          <>
            <span className={styles.kicker}>{etiquetaMedio}</span>
            <p className={styles.body}>{d.comoLlenarlo}</p>
          </>
        )}
        {d.ejemplo?.trim() && (
          <>
            <span className={styles.kicker}>Ejemplo</span>
            <p className={`${styles.body} ${styles.ejemplo}`}>{d.ejemplo}</p>
          </>
        )}
        {d.afecta.length > 0 && (
          <div className={styles.chips}>
            <span className={styles.chipsLabel}>Afecta</span>
            {d.afecta.map((chip) => (
              <span key={chip} className={styles.chip}>{chip}</span>
            ))}
          </div>
        )}
      </PopoverSurface>
    </Popover>
  );
}
